import {
  Paper,
  ShadowDivider,
  InfoTooltip,
  Box,
  Stack,
} from '@cast/design-system';
import { dashesOrNum } from '@cast/utils';

import { CostReportChart } from 'features/cost-report/components/chart/CostReportChart';
import { SmallMetricsBox } from 'features/cost-report/components/cost-over-time/SmallMetricsBox';
import { buildCostReportAnalyzePrompt } from 'features/cost-report/utils/buildCostReportAnalyzePrompt';
import { preparePodCount } from 'features/cost-report/utils/costOverTime';

import { usePodCountTooltip } from './_components/PodCountTooltip';
import { usePodCountChart, usePodCountChartData } from './_hooks';
import { CostReportChartToggle } from '../../../../../../cost-report/components/CostReportChartToggle';
import { ReportSection } from '../../../../../../cost-report/components/ReportSection';
import { useCostOverTime } from '../../../../../../cost-report/hooks/useCostOverTime';
import { useCostReportContext } from '../../../../../../cost-report/hooks/useCostReportContext';
import { useCostReportTimeRangeSelection } from '../../../../../../cost-report/hooks/useCostReportTimeRangeSelection';

import { useCluster } from '@cast/shared-cluster-context-data-access';

export const PodMetricsSection = () => {
  const { isDefaultFilter, chartType, timezone } = useCostReportContext();
  const { getRange } = useCostReportTimeRangeSelection();
  const { tooltipHeader } = usePodCountTooltip();
  const { isCollecting, aggregatedMetrics, dynamicCopiesMap } =
    useCostOverTime();
  const config = usePodCountChart();
  const { data: podCountData } = usePodCountChartData();
  const { cluster } = useCluster();

  const { podsCount } = aggregatedMetrics;

  const { total, onDemand, fallback, spot } = preparePodCount(
    podsCount?.onDemand,
    podsCount?.fallback,
    podsCount?.spot
  );

  const LOADING = '--';

  return (
    <ReportSection title="Pod fluctuation" testId="pod-metrics-section">
      <Paper sx={{ p: 0, mt: 8 }}>
        <Box display="flex" justifyContent="space-between" px="20px" py="14px">
          <Box flex={1}>
            <SmallMetricsBox
              title={
                <>
                  <span>
                    {isDefaultFilter
                      ? 'Current pod count'
                      : 'Average pod count'}
                  </span>
                  {isDefaultFilter && (
                    <InfoTooltip
                      title={dynamicCopiesMap.podCount?.average ?? ''}
                    />
                  )}
                </>
              }
              primaryValue={isCollecting ? LOADING : total}
            />
          </Box>
          <Stack flex={1} direction="row" justifyContent="space-between">
            <Box width="100%">
              <SmallMetricsBox
                testId="on-demand-pod-count"
                markerColor="blue.300"
                title="On-demand"
                primaryValue={isCollecting ? LOADING : onDemand}
              />
            </Box>
            <Box width="100%">
              <SmallMetricsBox
                testId="fallback-pod-count"
                title="Fallback"
                primaryValue={isCollecting ? LOADING : fallback}
                markerColor="yellow.400"
              />
            </Box>
            <Box width="100%">
              <SmallMetricsBox
                testId="spot-pod-count"
                markerColor="indigo.800"
                title="Spot"
                primaryValue={isCollecting ? LOADING : spot}
              />
            </Box>
          </Stack>
        </Box>
        <ShadowDivider opacity={0.2} />
        <Stack
          direction="row"
          justifyContent="flex-end"
          flexWrap="nowrap"
          width="100%"
          px={8}
          sx={{ transform: 'translateY(-12px)' }}
        >
          <CostReportChartToggle />
        </Stack>
        <CostReportChart
          timezone={timezone}
          testId="pod-count-chart"
          chartType={chartType}
          isLoading={isCollecting}
          tooltipHeader={tooltipHeader}
          tooltipFormatValue={(value) => dashesOrNum({ value })}
          data={podCountData}
          height={150}
          config={config}
          chartSx={{ px: 16 }}
          yAxisProps={{ tickCount: 3, padding: { top: 10 } }}
          buildPrompt={buildCostReportAnalyzePrompt({
            scopeName: cluster?.name,
            context: 'pod fluctuation',
            getDefaultRange: getRange,
          })}
        />
      </Paper>
    </ReportSection>
  );
};
